import {
  IconEyePlus,
  IconLoader2,
  IconMovie,
  IconPhotoFilled,
  IconUpload,
} from '@tabler/icons-react';
import useTheme from 'chat-ui/src/hooks/useTheme';
import { useState, useRef } from 'react';
import useImageViewerStore from 'chat-ui/src/store/ImageViewerStore';
import { toast } from 'react-toastify';
import RequiredLabel from '@/components/labels/RequiredLabel';
import { Input } from '@/components/ui/input';
import useTranslation from '@/hooks/useTranslation';
import useWorkspaceRoles from '@/hooks/useWorkspaceRoles';
import useCreateProjectStore from '@/store/CreateProjectStore';
import getTextColor from '@/utils/getTextColorContrast';
import { MediaUploadingProps } from '@/types';
import { Button } from '@/components/ui/button';
import DeleteQuestionMediaModal from '@/components/Modals/DeleteQuestionMediaModal';
import { cn } from '@/lib/utils.ts';

const MAX_FILE_SIZE = 1024 * 1024 * 200;

function QuestionMediaUploading({
  isProjectCompleted,
  isProjectPublished,
  index,
  setFileData,
  setValue,
  currentQuestion,
}: MediaUploadingProps) {
  const { lang } = useTranslation();
  const { currentTheme } = useTheme();
  const { isUserViewer } = useWorkspaceRoles();
  const { setIsFormChanged, setIsMediaReadyForUpload, uploadingQuestionId } =
    useCreateProjectStore();

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { setIsViewerOpen, setCurrentImageUrl, setCurrentImageAlt } = useImageViewerStore();

  const mediaType = currentQuestion?.media?.type;
  const isPDF = mediaType === 'application/pdf';
  const canPreview = !isPDF;

  const showImagePreview = () => {
    const imageURL: string | undefined = currentQuestion?.media?.url;
    if (!canPreview || !imageURL) return;

    setCurrentImageUrl(imageURL);
    setCurrentImageAlt(currentQuestion?.media?.name || 'image preview');
    setIsViewerOpen(true);
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file?.size > MAX_FILE_SIZE) {
      toast.error(lang.get('msg.maximum200MBfileError'));
      return;
    }

    setFileData((prevFileData) => ({
      ...prevFileData,
      [currentQuestion?.id]: {
        ...prevFileData[currentQuestion?.id],
        file,
      },
    }));
    setValue(`interview_questions[${index}].media`, file, { shouldDirty: true });
    setIsFormChanged(true);
    setIsMediaReadyForUpload(true);
  };

  const isActionGloballyDisabled = isUserViewer || isProjectPublished || isProjectCompleted;
  const isFileUploadDisabled =
    isActionGloballyDisabled || uploadingQuestionId === currentQuestion?.id;
  const isMediaDeleteDisabled = isActionGloballyDisabled;

  // Only show uploaded media (with URL), not locally selected files
  const hasUploadedMedia = !!(currentQuestion?.media && currentQuestion.media.url);
  const mediaName = currentQuestion?.media?.name;
  const isThisQuestionUploading = uploadingQuestionId === currentQuestion?.id;

  const onDeleteImage = () => {
    setOpenDeleteModal(true);
  };

  const handleDeleteSuccess = () => {
    setFileData((prevFileData) => ({
      ...prevFileData,
      [currentQuestion?.id]: {
        ...prevFileData[currentQuestion?.id],
        file: undefined,
      },
    }));
    setValue(`interview_questions[${index}].media`, null, { shouldDirty: true });
    setIsFormChanged(true);

    // Clear the file input value
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div>
      <RequiredLabel notRequired title="uploadMedia" tooltip={lang.get('msg.uploadMediaInfo')}>
        <div className="flex flex-col items-center w-full gap-4">
          <div className="flex items-center w-full gap-1.5 cursor-pointer">
            <IconUpload
              size={25}
              style={{
                backgroundColor: currentTheme?.primary,
                color: getTextColor(currentTheme?.primary || ''),
              }}
              className="w-11 h-[38px] p-1.5 rounded-md"
            />
            <Input
              ref={fileInputRef}
              accept="image/jpeg, image/jpg, video/mp4, audio/mp3, audio/wav, image/png, image/webp, image/gif, application/pdf"
              onChange={handleFileChange}
              disabled={isFileUploadDisabled}
              type="file"
              className="w-full cursor-pointer"
            />
          </div>
        </div>
      </RequiredLabel>

      {isThisQuestionUploading ? (
        <div className="flex items-center justify-center w-full gap-2 px-4 py-2 mt-3 border-2 border-dashed rounded-sm bg-primary/20 border-primary">
          <IconLoader2 size={20} className="animate-spin" />
          <p className="text-sm font-semibold text-primary">
            {lang.get('msg.uploading')}
            ...
          </p>
        </div>
      ) : (
        hasUploadedMedia &&
        !isThisQuestionUploading && (
          <div className="flex items-center mt-3 w-full gap-2">
            <button
              onClick={showImagePreview}
              type="button"
              className={cn(
                'flex flex-col flex-1 min-w-0 gap-1.5',
                canPreview ? 'cursor-pointer' : 'cursor-default',
              )}
            >
              <p className="relative flex items-center w-full gap-1 py-2 pl-4 pr-8 text-sm font-semibold border-2 border-dashed rounded-sm text-primary border-primary bg-primary/20">
                {currentQuestion?.media?.type === 'video/mp4' ? (
                  <IconMovie size={20} className="mr-1 min-w-5" />
                ) : (
                  <IconPhotoFilled size={20} className="mr-1 min-w-5" />
                )}
                <span className="overflow-hidden truncate flex-1 text-left">{mediaName}</span>
                {canPreview && (
                  <IconEyePlus
                    size={20}
                    className="absolute -translate-y-1/2 right-2 top-1/2 text-primary "
                  />
                )}
              </p>
            </button>
            <Button variant="destructive" onClick={onDeleteImage} disabled={isMediaDeleteDisabled}>
              {lang.get('msg.delete')}
            </Button>
          </div>
        )
      )}
      {/* delete modal */}
      <DeleteQuestionMediaModal
        isOpen={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        questionId={currentQuestion?.id}
        mediaName={mediaName || ''}
        onSuccess={handleDeleteSuccess}
        mediaId={currentQuestion?.media?._id || ''}
      />
    </div>
  );
}

export default QuestionMediaUploading;
